<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TeletelMetaStatusAPI from 'dashboard/api/teletel/metaStatus';
import Button from 'dashboard/components-next/button/Button.vue';

// «قالب جديد» on the Meta page (owner order 2026-09-27): write a WhatsApp
// template, see it as a phone bubble, and submit it to Meta for review. The
// server checks the same rules before calling Meta.
const props = defineProps({
  numbers: { type: Array, default: () => [] },
});
const emit = defineEmits(['created', 'close']);

const { t, te } = useI18n();

const blankButton = () => ({
  type: 'QUICK_REPLY',
  text: '',
  url: '',
  phone_number: '',
});
const form = ref({
  inbox_id: props.numbers[0]?.inbox_id || null,
  name: '',
  language: 'ar',
  category: 'MARKETING',
  header_text: '',
  body: '',
  footer: '',
  examples: [],
  buttons: [],
});
const saving = ref(false);

// {{1}} {{2}} ... found in the body, each needs one example value for Meta.
const variables = computed(() =>
  [
    ...new Set(
      (form.value.body.match(/\{\{(\d+)\}\}/g) || []).map(v =>
        Number(v.slice(2, -2))
      )
    ),
  ].sort((a, b) => a - b)
);
watch(variables, list => {
  form.value.examples = list.map((_, i) => form.value.examples[i] || '');
});

// Meta allows lower-case letters, digits and underscores only.
const normaliseName = () => {
  form.value.name = form.value.name
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
};

// Written without a literal double brace: the template parser would end
// the interpolation there.
const varLabel = n => ['{', '{', n, '}', '}'].join('');

const addVariable = () => {
  const next = (variables.value.at(-1) || 0) + 1;
  form.value.body = `${form.value.body}{{${next}}}`;
};

const addButton = () => {
  if (form.value.buttons.length < 3) form.value.buttons.push(blankButton());
};
const removeButton = index => form.value.buttons.splice(index, 1);

const previewBody = computed(() =>
  form.value.body.replace(
    /\{\{(\d+)\}\}/g,
    (match, n) =>
      form.value.examples[variables.value.indexOf(Number(n))] || match
  )
);

const canSubmit = computed(
  () =>
    form.value.inbox_id &&
    form.value.name &&
    form.value.body.trim() &&
    !saving.value
);

const submit = async () => {
  saving.value = true;
  try {
    const { data } = await TeletelMetaStatusAPI.createTemplate({
      ...form.value,
      buttons: form.value.buttons.filter(b => b.text.trim()),
    });
    useAlert(
      t('TELETEL_META.COMPOSER.SUBMITTED', {
        status: data.result?.status || '',
      })
    );
    emit('created', data.templates || []);
  } catch (error) {
    const code = error?.response?.data?.error || '';
    if (code.startsWith('META:')) {
      useAlert(
        t('TELETEL_META.COMPOSER.ERRORS.META', { detail: code.slice(5) })
      );
    } else {
      const key = `TELETEL_META.COMPOSER.ERRORS.${code}`;
      useAlert(t(te(key) ? key : 'TELETEL_META.COMPOSER.ERRORS.GENERIC'));
    }
  } finally {
    saving.value = false;
  }
};

const inputClass =
  '!mb-0 w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm';
</script>

<template>
  <div
    class="grid gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 lg:grid-cols-[1fr_300px]"
  >
    <div class="flex flex-col gap-3 text-sm">
      <h3 class="text-base font-semibold text-n-slate-12">
        {{ t('TELETEL_META.COMPOSER.TITLE') }}
      </h3>
      <p class="text-n-slate-11">{{ t('TELETEL_META.COMPOSER.HELP') }}</p>

      <div class="grid gap-3 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          {{ t('TELETEL_META.COMPOSER.NUMBER') }}
          <select v-model.number="form.inbox_id" :class="inputClass">
            <option v-for="n in numbers" :key="n.inbox_id" :value="n.inbox_id">
              {{ n.inbox_name }} ({{ n.phone_number }})
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          {{ t('TELETEL_META.COMPOSER.NAME') }}
          <input
            v-model="form.name"
            dir="ltr"
            :class="inputClass"
            :placeholder="t('TELETEL_META.COMPOSER.NAME_PLACEHOLDER')"
            @input="normaliseName"
          />
          <span class="text-xs text-n-slate-11">{{
            t('TELETEL_META.COMPOSER.NAME_HINT')
          }}</span>
        </label>
        <label class="flex flex-col gap-1">
          {{ t('TELETEL_META.COMPOSER.LANGUAGE') }}
          <select v-model="form.language" :class="inputClass">
            <option value="ar">{{ t('TELETEL_META.COMPOSER.LANG_AR') }}</option>
            <option value="en">{{ t('TELETEL_META.COMPOSER.LANG_EN') }}</option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          {{ t('TELETEL_META.COMPOSER.CATEGORY') }}
          <select v-model="form.category" :class="inputClass">
            <option value="MARKETING">
              {{ t('TELETEL_META.COMPOSER.CAT_MARKETING') }}
            </option>
            <option value="UTILITY">
              {{ t('TELETEL_META.COMPOSER.CAT_UTILITY') }}
            </option>
          </select>
        </label>
      </div>

      <label class="flex flex-col gap-1">
        {{ t('TELETEL_META.COMPOSER.HEADER') }}
        <input v-model="form.header_text" maxlength="60" :class="inputClass" />
      </label>

      <label class="flex flex-col gap-1">
        <span class="flex items-center justify-between">
          {{ t('TELETEL_META.COMPOSER.BODY') }}
          <Button
            size="xs"
            color="slate"
            variant="link"
            :label="t('TELETEL_META.COMPOSER.ADD_VARIABLE')"
            @click="addVariable"
          />
        </span>
        <textarea
          v-model="form.body"
          rows="5"
          maxlength="1024"
          :class="inputClass"
        />
        <span class="text-xs text-n-slate-11">{{
          t('TELETEL_META.COMPOSER.BODY_HINT')
        }}</span>
      </label>

      <div v-if="variables.length" class="flex flex-col gap-2">
        <span>{{ t('TELETEL_META.COMPOSER.EXAMPLES') }}</span>
        <label
          v-for="(v, i) in variables"
          :key="v"
          class="flex items-center gap-2"
        >
          <span class="w-12 text-n-slate-11" dir="ltr">{{ varLabel(v) }}</span>
          <input v-model="form.examples[i]" :class="inputClass" />
        </label>
      </div>

      <label class="flex flex-col gap-1">
        {{ t('TELETEL_META.COMPOSER.FOOTER') }}
        <input v-model="form.footer" maxlength="60" :class="inputClass" />
      </label>

      <div class="flex flex-col gap-2">
        <span class="flex items-center justify-between">
          {{ t('TELETEL_META.COMPOSER.BUTTONS') }}
          <Button
            v-if="form.buttons.length < 3"
            size="xs"
            color="slate"
            variant="link"
            :label="t('TELETEL_META.COMPOSER.ADD_BUTTON')"
            @click="addButton"
          />
        </span>
        <div
          v-for="(b, i) in form.buttons"
          :key="i"
          class="flex flex-wrap items-center gap-2"
        >
          <select v-model="b.type" :class="inputClass" class="!w-auto">
            <option value="QUICK_REPLY">
              {{ t('TELETEL_META.COMPOSER.BTN_QUICK') }}
            </option>
            <option value="URL">
              {{ t('TELETEL_META.COMPOSER.BTN_URL') }}
            </option>
            <option value="PHONE_NUMBER">
              {{ t('TELETEL_META.COMPOSER.BTN_PHONE') }}
            </option>
          </select>
          <input
            v-model="b.text"
            maxlength="25"
            :class="inputClass"
            class="!w-40"
            :placeholder="t('TELETEL_META.COMPOSER.BTN_TEXT')"
          />
          <input
            v-if="b.type === 'URL'"
            v-model="b.url"
            dir="ltr"
            :class="inputClass"
            class="!w-56"
            :placeholder="t('TELETEL_META.COMPOSER.URL_PLACEHOLDER')"
          />
          <input
            v-if="b.type === 'PHONE_NUMBER'"
            v-model="b.phone_number"
            dir="ltr"
            :class="inputClass"
            class="!w-44"
            :placeholder="t('TELETEL_META.COMPOSER.PHONE_PLACEHOLDER')"
          />
          <Button
            size="xs"
            color="ruby"
            variant="link"
            icon="i-lucide-x"
            @click="removeButton(i)"
          />
        </div>
        <span class="text-xs text-n-slate-11">{{
          t('TELETEL_META.COMPOSER.BUTTONS_HINT')
        }}</span>
      </div>

      <div class="flex gap-2">
        <Button
          :label="t('TELETEL_META.COMPOSER.SUBMIT')"
          icon="i-lucide-send"
          :is-loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        />
        <Button
          color="slate"
          variant="faded"
          :label="t('TELETEL_META.COMPOSER.CANCEL')"
          @click="emit('close')"
        />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm text-n-slate-11">{{
        t('TELETEL_META.COMPOSER.PREVIEW')
      }}</span>
      <div class="rounded-xl bg-n-teal-2 p-3">
        <div
          class="max-w-[260px] rounded-lg bg-n-solid-1 p-2 text-sm shadow-sm"
          :dir="form.language === 'ar' ? 'rtl' : 'ltr'"
        >
          <p v-if="form.header_text" class="mb-1 font-semibold text-n-slate-12">
            {{ form.header_text }}
          </p>
          <p class="whitespace-pre-wrap text-n-slate-12">
            {{ previewBody || '…' }}
          </p>
          <p v-if="form.footer" class="mt-1 text-xs text-n-slate-11">
            {{ form.footer }}
          </p>
        </div>
        <div
          v-for="(b, i) in form.buttons.filter(x => x.text.trim())"
          :key="i"
          class="mt-1 max-w-[260px] rounded-lg bg-n-solid-1 p-1.5 text-center text-sm text-n-blue-11 shadow-sm"
        >
          {{ b.text }}
        </div>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ t('TELETEL_META.COMPOSER.REVIEW_NOTE') }}
      </p>
    </div>
  </div>
</template>
